// The guests panel (protocol 1.8): one section per guest with what they said and when, and a
// way to name them. Naming is for this session; remembering their voice (so the kit recognises
// them next time) is a separate, explicit choice with a consent note, because a voiceprint is
// biometric data. Rendered incrementally, so a name being typed survives new speech arriving.

import { consentNote, SOMEONE_ID, type Guest, type GuestLine, type GuestModel } from "./guests";
import { clipButton, type ClipPlayer } from "./mine";
import { injectStyles } from "./styles";

export interface GuestActions {
  /** Name a guest (`name` empty: back to "Guest n"); `save` remembers their voice. */
  rename(guestId: string, name: string, save: boolean): Promise<void>;
  /** Forget a guest of the session (and their saved voice, if any). */
  forget(guestId: string): Promise<void>;
  /**
   * 1.10, "That was me": this line was the user, not the guest (`client.speechMine`). Offered on
   * lines the kit gave an id; without it, no button.
   */
  thatWasMe?(ignoredId: string): Promise<void>;
  /** 1.10: plays a line's clip (a play/stop button beside "That was me"). */
  clips?: ClipPlayer;
  /**
   * Whether this UI may act on `ignoredId` (a phone only on speech heard on its own mic): false
   * leaves out both Play and "That was me". Without it, every line with an id offers them.
   */
  mayAct?(ignoredId: string): boolean;
}

/** A "That was me" button for ignored speech `ignoredId` (guests panel and transcript). */
export function thatWasMeButton(ignoredId: string, act: (id: string) => Promise<void>): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "vk-mine";
  b.dataset.ignored = ignoredId;
  b.textContent = "That was me";
  b.title = "This was you, not someone else: show it as yours and send it to the assistant";
  b.addEventListener("click", (e) => {
    e.stopPropagation();
    b.disabled = true;
    act(ignoredId).catch(() => (b.disabled = false));
  });
  return b;
}

export interface GuestsView {
  destroy(): void;
}

/** Hours and minutes, in the page's locale. */
export function lineTime(at: number): string {
  return new Date(at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/** The status line under a guest's name. */
export function guestStatus(g: Guest): string {
  if (g.id === SOMEONE_ID) return "Too short to tell whose";
  if (g.ended) return g.saved ? "Remembered · from the last session" : "From the last session";
  if (g.saved) return "Voice remembered";
  if (g.savePending) return "Will be remembered once I've heard a little more";
  return "This session only";
}

interface Rendered {
  section: HTMLElement;
  label: HTMLElement;
  status: HTMLElement;
  edit: HTMLButtonElement;
  form: HTMLFormElement;
  lines: HTMLOListElement;
  /** The lines rendered, in order (a line gone from the middle, or scrolled out, rebuilds). */
  shown: GuestLine[];
}

/**
 * Mount the guests panel into `root`. `actions` talks to the kit (e.g. `client.guestRename`,
 * `client.guestForget`); errors are shown on the guest's form.
 */
export function mountGuests(root: HTMLElement, model: GuestModel, actions: GuestActions): GuestsView {
  injectStyles();
  root.classList.add("vk-guests-panel");
  const head = document.createElement("header");
  head.className = "vk-guests-head";
  head.innerHTML = `<h3>Guests</h3><p>Other voices, grouped by voice. Never sent to the assistant.</p>`;
  const list = document.createElement("div");
  list.className = "vk-guests-list";
  const empty = document.createElement("p");
  empty.className = "vk-guests-empty";
  empty.textContent = "When “Only listen to me” ignores someone, what they said appears here.";
  root.replaceChildren(head, list, empty);

  const rendered = new Map<string, Rendered>();

  const build = (g: Guest): Rendered => {
    const section = document.createElement("section");
    section.className = "vk-guest";
    section.dataset.guest = g.id;
    const top = document.createElement("div");
    top.className = "vk-guest-top";
    const who = document.createElement("div");
    who.className = "vk-guest-who";
    const label = document.createElement("b");
    label.className = "vk-guest-label";
    const status = document.createElement("span");
    status.className = "vk-guest-status";
    who.append(label, status);
    const edit = document.createElement("button");
    edit.type = "button";
    edit.className = "vk-guest-edit";
    edit.textContent = "Name";
    top.append(who, edit);

    const form = document.createElement("form");
    form.className = "vk-guest-form";
    form.hidden = true;
    form.innerHTML = `
      <label class="vk-guest-field"><span>Name</span><input name="name" maxlength="64" autocomplete="off" spellcheck="false" /></label>
      <label class="vk-guest-save"><input type="checkbox" name="save" /> <span>Remember their voice</span></label>
      <p class="vk-guest-consent" hidden></p>
      <div class="vk-guest-actions">
        <button type="submit" class="vk-primary">Save</button>
        <button type="button" data-act="cancel">Cancel</button>
        <button type="button" data-act="forget" class="vk-danger">Forget</button>
      </div>
      <p class="vk-guest-error" role="alert"></p>`;
    const nameIn = form.elements.namedItem("name") as HTMLInputElement;
    const saveIn = form.elements.namedItem("save") as HTMLInputElement;
    const consent = form.querySelector<HTMLParagraphElement>(".vk-guest-consent")!;
    const submit = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
    const error = form.querySelector<HTMLParagraphElement>(".vk-guest-error")!;
    // The consent note shows while "remember" is ticked for someone not yet remembered.
    const syncConsent = () => {
      const cur = model.find(g.id);
      const asking = saveIn.checked && !cur?.saved;
      consent.hidden = !asking;
      consent.textContent = asking ? consentNote(nameIn.value) : "";
      submit.textContent = asking ? "Save and remember" : "Save";
    };
    nameIn.addEventListener("input", syncConsent);
    saveIn.addEventListener("change", syncConsent);
    edit.addEventListener("click", () => {
      const cur = model.find(g.id);
      if (!cur) return;
      form.hidden = !form.hidden;
      if (!form.hidden) {
        nameIn.value = cur.name ?? "";
        saveIn.checked = cur.saved || cur.savePending;
        saveIn.disabled = cur.saved;
        error.textContent = "";
        syncConsent();
        nameIn.focus();
      }
    });
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const cur = model.find(g.id);
      if (!cur) return;
      const name = nameIn.value.trim();
      const save = saveIn.checked && !cur.saved;
      if (save && !name) {
        error.textContent = "Give them a name to remember their voice.";
        nameIn.focus();
        return;
      }
      error.textContent = "";
      submit.disabled = true;
      actions.rename(g.id, name, save || (cur.savePending && saveIn.checked)).then(
        () => (form.hidden = true),
        (err: Error) => (error.textContent = err.message),
      ).finally(() => (submit.disabled = false));
    });
    form.addEventListener("click", (e) => {
      const act = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-act]")?.dataset.act;
      if (act === "cancel") form.hidden = true;
      if (act === "forget") {
        actions.forget(g.id).then(
          () => model.remove(g.id),
          (err: Error) => (error.textContent = err.message),
        );
      }
    });

    const lines = document.createElement("ol");
    lines.className = "vk-guest-lines";
    lines.setAttribute("aria-label", "What they said");
    section.append(top, form, lines);
    return { section, label, status, edit, form, lines, shown: [] };
  };

  const lineNode = (line: GuestLine): HTMLLIElement => {
    const li = document.createElement("li");
    const time = document.createElement("time");
    time.dateTime = new Date(line.at).toISOString();
    time.textContent = lineTime(line.at);
    const words = document.createElement("span");
    if (line.text) words.textContent = line.text;
    else {
      words.className = "vk-guest-nowords";
      words.textContent = "spoke";
    }
    li.append(time, words);
    if (line.ignoredId && (actions.thatWasMe || actions.clips) && (actions.mayAct?.(line.ignoredId) ?? true)) {
      const acts = document.createElement("span");
      acts.className = "vk-line-actions";
      if (actions.clips) acts.append(clipButton(line.ignoredId, actions.clips));
      if (actions.thatWasMe) acts.append(thatWasMeButton(line.ignoredId, actions.thatWasMe));
      li.append(acts);
    }
    return li;
  };

  const update = (r: Rendered, g: Guest) => {
    r.label.textContent = g.label;
    r.status.textContent = guestStatus(g);
    r.section.classList.toggle("ended", g.ended);
    r.section.classList.toggle("saved", g.saved);
    const canName = g.id !== SOMEONE_ID && !g.ended;
    r.edit.hidden = !canName;
    if (!canName) r.form.hidden = true;
    r.edit.textContent = g.name ? "Rename" : "Name";
    const same = r.shown.length <= g.lines.length && r.shown.every((l, i) => l === g.lines[i]);
    if (!same) {
      r.lines.replaceChildren();
      r.shown = [];
    }
    for (let i = r.shown.length; i < g.lines.length; i++) {
      r.lines.append(lineNode(g.lines[i]));
      r.shown.push(g.lines[i]);
    }
  };

  const render = () => {
    const shown = model.visible;
    const keep = new Set(shown.map((g) => g.id));
    for (const [id, r] of rendered) {
      if (!keep.has(id)) {
        r.section.remove();
        rendered.delete(id);
      }
    }
    let prev: Element | null = null;
    for (const g of shown) {
      let r = rendered.get(g.id);
      if (!r) rendered.set(g.id, (r = build(g)));
      update(r, g);
      const expected: Element | null = prev ? prev.nextElementSibling : list.firstElementChild;
      if (expected !== r.section) {
        if (prev) prev.after(r.section);
        else list.prepend(r.section);
      }
      prev = r.section;
    }
    empty.hidden = shown.length > 0;
  };

  const off = model.onChange(render);
  render();
  return {
    destroy() {
      off();
      root.replaceChildren();
      root.classList.remove("vk-guests-panel");
    },
  };
}
